/**
 * API Requests follow the project scope (card 2.1), pure:
 *
 *   project → workspace   a project scope that resolves to a project whose
 *                         workspaces do not include the open one opens the
 *                         one last used in it, else its first (`boundWorkspaces`
 *                         re-checks the team-sharing rule).
 *   workspace → project   picking a workspace by hand moves the scope to a
 *                         project holding it — personal first, then by name —
 *                         or leaves it alone when the active project holds it
 *                         or none does.
 *
 * Both sides return null for "nothing to do", so the caller never switches to
 * what is already open (no loop between the two).
 *
 * `followScope` is the full project → workspace rule (26.10.2026, owner: the
 * API client kept another project's workspace after a project switch):
 *
 *   a project / No project   the open workspace stays when the scope lists it
 *                            (scope/projectWorkspaceList — the SAME filter the
 *                            switcher's Workspaces submenu shows); else the
 *                            one last used in that scope, else its first;
 *                            a scope with no workspace CLEARS the open one
 *                            (empty API client, never a foreign workspace).
 *   All projects             keeps the open workspace; only when none is open
 *                            does it open the last used under All, else the
 *                            first.
 *   pending                  nothing (the project may resolve in a moment).
 */
import { boundWorkspaces } from './projectBinding';
import { holdsWorkspace } from './projectBindingFields';
import { ALL_PROJECTS, NO_PROJECT, normalizeScopeId } from './projectScopeFilter';
import { workspacesForScope } from './projectWorkspaceList';

const isTeam = (p) => String(p?.scopeKey || '').startsWith('team:');

/**
 * The workspace the active project wants open, when the open one is not one
 * of its own: the one last used in it (`lastUsedId`, per device), else its
 * first. A project holds several workspaces since 21.10.2026.
 */
export function workspaceToOpen({ scope, workspaces = [], currentWorkspaceId = null, lastUsedId = null }) {
  if (!scope || scope.status !== 'ready' || !scope.activeProject) return null;
  const own = boundWorkspaces(scope.activeProject, workspaces);
  if (!own.length) return null;
  if (currentWorkspaceId != null && own.some((w) => String(w.id) === String(currentWorkspaceId))) return null;
  return own.find((w) => lastUsedId != null && String(w.id) === String(lastUsedId)) || own[0];
}

/** The project a hand-picked workspace belongs to, when the scope is not already on one holding it. */
export function projectForWorkspace({ scope, projects = [], workspaces = [], workspaceId = null }) {
  if (workspaceId == null) return null;
  const id = String(workspaceId);
  if (holdsWorkspace(scope?.activeProject, id)) return null;
  const candidates = projects
    .filter((p) => holdsWorkspace(p, id) && boundWorkspaces(p, workspaces).some((w) => String(w.id) === id))
    .sort((a, b) => Number(isTeam(a)) - Number(isTeam(b)) || String(a.name || '').localeCompare(String(b.name || '')));
  return candidates[0] || null;
}

/**
 * What the active scope wants open: null = nothing to do, `{ workspace }` =
 * open it, `{ workspace: null }` = close the open one (the scope has none).
 *
 * `projectsLoaded` = the known projects are loaded; until then "No project"
 * cannot tell which workspaces are unassigned, so it does nothing. An empty
 * workspace list (not loaded yet) never clears.
 */
export function followScope({
  scope,
  projects = [],
  workspaces = [],
  currentWorkspaceId = null,
  lastUsedId = null,
  projectsLoaded = true,
}) {
  if (!scope || scope.status !== 'ready' || !(workspaces || []).length) return null;
  const id = normalizeScopeId(scope.activeProjectId);
  if (id === NO_PROJECT && !projectsLoaded) return null;
  if (id !== ALL_PROJECTS && id !== NO_PROJECT && !scope.activeProject) return null;
  const own = workspacesForScope({ scope, projects, workspaces });
  const hasCurrent = currentWorkspaceId != null;
  if (hasCurrent && (id === ALL_PROJECTS || own.some((w) => String(w.id) === String(currentWorkspaceId)))) return null;
  if (!own.length) return hasCurrent ? { workspace: null } : null;
  return { workspace: own.find((w) => lastUsedId != null && String(w.id) === String(lastUsedId)) || own[0] };
}
